import { useEffect, useRef } from "react";

/** Long-press threshold — long enough to scroll, short enough to feel instant. */
export const LONG_PRESS_MS = 450;
export const LONG_PRESS_MOVE_PX = 12;

type PressState = {
  timer: number | null;
  x: number;
  y: number;
  fired: boolean;
  fire: (() => void) | null;
};

/**
 * Shared long-press controller for touch/pen surfaces (chat list rows, message
 * bubbles). One instance per pane — only one press can be active at a time,
 * which mirrors how the menus behave anyway.
 *
 * Desktop parity: wire an `onContextMenu` handler that preventDefaults and
 * opens the same menu, calling `cancel()` first so a right-click can't race a
 * pending long-press timer.
 */
export function useLongPress() {
  const press = useRef<PressState>({ timer: null, x: 0, y: 0, fired: false, fire: null });

  useEffect(() => {
    const state = press.current;
    return () => {
      if (state.timer !== null) window.clearTimeout(state.timer);
    };
  }, []);

  function cancel() {
    if (press.current.timer !== null) {
      window.clearTimeout(press.current.timer);
      press.current.timer = null;
    }
  }

  function begin(
    e: React.PointerEvent,
    fire: () => void,
    shouldStart?: (e: React.PointerEvent) => boolean,
  ) {
    if (shouldStart && !shouldStart(e)) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    cancel();
    press.current.fired = false;
    press.current.fire = fire;
    press.current.x = e.clientX;
    press.current.y = e.clientY;
    press.current.timer = window.setTimeout(() => {
      press.current.timer = null;
      press.current.fired = true;
      navigator.vibrate?.(15);
      press.current.fire?.();
    }, LONG_PRESS_MS);
  }

  function move(e: React.PointerEvent) {
    if (press.current.timer === null) return;
    const dx = e.clientX - press.current.x;
    const dy = e.clientY - press.current.y;
    if (Math.hypot(dx, dy) > LONG_PRESS_MOVE_PX) cancel();
  }

  /** True if the press that just ended fired the menu — swallow the trailing click. */
  function consumeFired(): boolean {
    const fired = press.current.fired;
    press.current.fired = false;
    return fired;
  }

  return { begin, move, cancel, consumeFired, end: cancel };
}
